import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginRequest, User } from '../../types/auth';
import { apiClient } from '../../utils/apiClient';

// Initialize state with stored data if available
const getInitialState = (): AuthState => {
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');
  
  return {
    user: storedUser ? JSON.parse(storedUser) : null,
    token,
    isAuthenticated: !!(token && storedUser), // Only authenticate if both token and user exist
    loading: false, // Don't start with loading state
    error: null,
  };
};

const initialState: AuthState = getInitialState();

// Real login implementation using API client
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.login(credentials.username, credentials.password);
      
      // Set the token in the API client for subsequent requests
      apiClient.setAuthToken(response.token);
      
      return response;
    } catch (error: any) {
      // Ensure we return a proper error message
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Real logout implementation using API client
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      const response = await apiClient.logout();
      return response.success;
    } catch (error: any) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed, continuing with local logout');
      return true;
    }
  }
);

// Verify token validity and get user info
export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        return rejectWithValue({ message: 'No token or user found' });
      }

      // Set the token in API client
      apiClient.setAuthToken(token);
      
      // Simply return the stored user data without making API calls
      // The dashboard hook will handle 401 errors if the token is actually invalid
      return JSON.parse(storedUser);
    } catch (error: any) {
      return rejectWithValue({ message: error.message || 'Token verification failed' });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
    },
    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      apiClient.clearAuthToken();
    },
  },
  extraReducers: (builder) => {
    builder      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        // Don't clear error here - let it persist until login succeeds or user starts typing
        // state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        // Ensure token is set in API client
        apiClient.setAuthToken(action.payload.token);
      })      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        // Ensure error is in the right format for the UI
        const error = action.payload as any;
        if (error && error.message) {
          state.error = error.message;
        } else if (typeof error === 'string') {
          state.error = error;
        } else {
          state.error = 'Login failed. Please check your credentials.';
        }
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        apiClient.clearAuthToken();
      })      // Verify token
      .addCase(verifyToken.pending, (state) => {
        // Only set loading if user is not currently authenticated
        // This prevents UI flickering for already logged-in users
        if (!state.isAuthenticated) {
          state.loading = true;
        }
      })      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })      .addCase(verifyToken.rejected, (state, action) => {
        state.loading = false;
        // Only clear credentials if the error indicates the token is invalid
        // This prevents clearing valid sessions due to network issues
        const error = action.payload as any;
        if (error?.message?.includes('Token verification failed') || 
            error?.message?.includes('No token found')) {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          apiClient.clearAuthToken();
        }
      });
  },
});

export const { clearError, setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;
